import { get_companies } from "../services";
import React, { useState, useEffect } from "react";
import {
	Col,
	message,
	DatePicker,
	Row,
	Divider,
	Button,
	theme
} from "antd";
import PropTypes from "prop-types";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveCirclePacking } from "@nivo/circle-packing";
import { CircleAnimationCompact } from "../components/loading";
import { schemeBlues, schemeOranges, schemeGreens } from "d3-scale-chromatic";
import { formatNumber } from "../utils/CalculateInvoiceTotal";
import { WaitingForACompany } from "../components/WaitingForACompany";
const { RangePicker } = DatePicker;

const reversedGreens = [...schemeGreens[9]].reverse();
const reversedOranges = [...schemeOranges[9]].reverse();
const reversedBlues = [...schemeBlues[6]].reverse();

export const DashBoardStart = ({ token, company }) => {

	const { token: antToken } = theme.useToken();

	const [graphsData, setGraphsData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [startDate, setStartDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().slice(0, 10));
	const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
	const [zoomedId, setZoomedId] = useState(null);

	const getGraphsData = async () => {
		if (company.tid == "" || company.tid == undefined) return;
		setLoading(true);
		try {
			const response = await get_companies(
				token,
				"status",
				{
					id: company.id,
					tid: company.tid,
					startDate: startDate,
					endDate: endDate
				}
			);
			setLoading(false);
			return response;
		} catch (error) {
			message.error(error.response.data);
			setGraphsData(null);
			setLoading(false);
			return null;
		}
	};

	useEffect(() => {
	}, [graphsData]);

	function convertDataToNivoFormat(compras, ventas) {
		// Crear un objeto para facilitar la combinación de datos
		const dataMap = {};

		// Procesar compras
		compras.forEach((compra) => {
			const { month, total_sum } = compra;
			if (!dataMap[month]) {
				dataMap[month] = { date: month, compras: 0, ventas: 0 };
			}
			dataMap[month].compras = total_sum;
		});

		// Procesar ventas
		ventas.forEach((venta) => {
			const { month, total_sum } = venta;
			if (!dataMap[month]) {
				dataMap[month] = { date: month, compras: 0, ventas: 0 };
			}
			dataMap[month].ventas = total_sum;
		});

		// Convertir el objeto en un array
		const result = Object.values(dataMap).map((entry) => ({
			date: entry.date,
			compras: entry.compras,
			ventas: entry.ventas,
		}));

		return result;
	}

	const structureTop2 = (data, position = "in") => {
		return data.map(element => {
			const shortName = position == "in" ? element.sender_name.split(" ").map(word => word[0]).join("") : element.receptor_name.split(" ").map(word => word[0]).join("");

			return {
				id: shortName,
				label: shortName,
				longLabel: position == "in" ? element.sender_name : element.receptor_name,
				value: parseFloat(element.total_sum).toFixed(2)
			};
		});
	};

	function structureMonthlyClientRanking(monthlyClientRanking, position = "in") {

		// Crear un objeto para agrupar por iniciales
		const groupedData = {};

		// Recopilar todos los meses únicos
		const allMonths = Array.from(new Set(monthlyClientRanking.map(item => item.month))).sort();

		// Agrupar los datos por iniciales y completar con los meses que falten
		monthlyClientRanking.forEach(item => {
			const { month, receptor_name, sender_name, total_sum_gtq } = item;
			const name = position == "in" ? receptor_name : sender_name;

			// Si no existe el grupo para esas iniciales, inicializarlo
			if (!groupedData[name]) {
				groupedData[name] = { id: name, name: name, data: [] };
			}

			// Añadir el dato del mes
			groupedData[name].data.push({
				x: month,
				y: parseFloat(total_sum_gtq).toFixed(2),
				xLabel: name
			});
		});

		// Ahora recorrer cada grupo y asegurarse de que tenga valores para todos los meses
		Object.values(groupedData).forEach(group => {
			const existingMonths = group.data.map(d => d.x);

			allMonths.forEach(month => {
				// Si el mes no está en el grupo, agregarlo con y = null
				if (!existingMonths.includes(month)) {
					group.data.push({
						x: month,
						y: 0,
						xLabel: group.name
					});
				}
			});

			// Ordenar los datos por mes
			group.data.sort((a, b) => allMonths.indexOf(a.x) - allMonths.indexOf(b.x));
		});

		// Crear una lista de diccionarios con el campo month y los valores de cada grupo
		const result = allMonths.map(month => {
			const entry = { month };
			Object.values(groupedData).forEach(group => {
				const dataForMonth = group.data.find(d => d.x === month);
				entry[group.name] = dataForMonth ? parseFloat(dataForMonth.y) : 0;
			});
			return entry;
		});

		return result;
	}

	const structureTaxOverview = (data, position = "in") => {
		const response = {
			name: position == "in" ? "Ventas" : "Compras",
			children: data.map(tax => ({
				name: tax.tax_name,
				loc: tax.tax_total_in_gtq,
				value: tax.tax_total_in_gtq
			}))

		};
		return response;
	};


	const handleSearch = () => {
		const fetchData = async () => {
			const data = await getGraphsData();

			if (company.id == "") return;

			if (!data) {
				message.info("No se encontró Información para las gráficas");
				setGraphsData(null);
				return;
			}

			if (!data.expensesByMonth || !data.salesByMonth.length) {
				message.info("No se encontró Información para las gráficas");
				setGraphsData(null);
				return;
			}

			if (data.expensesByMonth.length >= 1 && data.salesByMonth.length >= 1) {
				setGraphsData(data);
			}
		};

		fetchData();
	};

	useEffect(() => {
		setGraphsData(null);
		const fetchData = async () => {
			const data = await getGraphsData();

			if (company.id == "") return;

			if (!data) {
				message.info("No se encontró Información para las gráficas");
				setGraphsData(null);
				return;
			}

			if (!data.expensesByMonth || !data.salesByMonth.length) {
				message.info("No se encontró Información para las gráficas");
				setGraphsData(null);
				return;
			}

			if (data.expensesByMonth.length >= 1 && data.salesByMonth.length >= 1) {
				setGraphsData(data);
			}
		};

		fetchData();
	}, [company]);

	function sumarValoresTotales(array) {
		let sumaTotal = 0;

		for (let i = 0; i < array.length; i++) {
			sumaTotal += array[i].total_sum;
		}

		return parseFloat(sumaTotal).toFixed(2);
	}


	return (
		<>
			<WaitingForACompany company_id={company.id}/>
			{
				loading &&
				<CircleAnimationCompact />
			}
			{
				graphsData != null && !loading && company.id != "" &&
				<>
					<RangePicker
						style={{
							marginRight: "2vw"
						}}
						onChange={(value, dateString) => {
							setStartDate(dateString[0]);
							setEndDate(dateString[1]);
						}}
					/>
					<Button
						type="primary"
						onClick={handleSearch}
					>Buscar</Button>
					<Row>
						<Col span={17}>
							<Divider style={{ margin: 0 }}>Compras vs Ventas</Divider>
						</Col>
						<Col
							span={16}
							style={{
								height: "35vh",
								background: antToken.colorBgLayout,
								borderRadius: 10
							}}
						>
							<ResponsiveBar
								data={convertDataToNivoFormat(graphsData.expensesByMonth || [], graphsData.salesByMonth || [])}
								keys={[
									"ventas",
									"compras"
								]}
								groupMode="grouped"
								indexBy="date"
								margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
								padding={0.3}
								borderRadius={5}
								valueScale={{ type: "linear" }}
								indexScale={{ type: "band", round: true }}
								enableLabel={false}
								colors={reversedBlues}
								borderColor={{
									from: "color",
									modifiers: [
										[
											"darker",
											1.6
										]
									]
								}}
								axisTop={null}
								axisRight={null}
								axisBottom={{
									tickSize: 5,
									tickPadding: 5,
									tickRotation: 0,
									legend: "Mes",
									legendPosition: "middle",
									legendOffset: 32,
									truncateTickAt: 0
								}}
								animate={false}
								axisLeft={{
									tickSize: 5,
									tickPadding: 0,
									tickRotation: 0,
									legend: "Total",
									legendPosition: "middle",
									legendOffset: -40,
									truncateTickAt: 0
								}}
								labelSkipWidth={12}
								labelSkipHeight={12}
								labelTextColor={{
									from: "color",
									modifiers: [
										[
											"darker",
											1.6
										]
									]
								}}
								legends={[
									{
										dataFrom: "keys",
										anchor: "bottom-right",
										direction: "column",
										justify: false,
										translateX: 120,
										translateY: 0,
										itemsSpacing: 2,
										itemWidth: 100,
										itemHeight: 20,
										itemDirection: "left-to-right",
										itemOpacity: 0.85,
										symbolSize: 20,
										effects: [
											{
												on: "hover",
												style: {
													itemOpacity: 1
												}
											}
										]
									}
								]}
								tooltip={(point) =>
									<div
										style={{
											padding: 12,
											color: "#000c17",
											background: "white"
										}}
									>{point.id} - GTQ {formatNumber(point.value)}</div>
								}
							/>						
						</Col>
						<Col span={3} style={{ marginLeft: "2.5vw" }}>
							<Row>
								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										marginBottom: "2.5vh",
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Facturas de Venta</p>
									<h3>{graphsData.salesInvoiceCount[0].invoice_count}</h3>
								</Col>

								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										marginBottom: "2.5vh",
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Facturas de Venta Anuladas</p>
									<h3>{graphsData.salesCanceledInvoiceCount[0].invoice_count}</h3>
								</Col>

								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Facturas de Venta Excluídas (Contabilidad)</p>
									<h3>{graphsData.salesExcludedInvoiceCount[0].invoice_count}</h3>
								</Col>
							</Row>
						</Col>
						<Col span={3} style={{ marginLeft: "2.5vw" }}>
							<Row>
								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										marginBottom: "2.5vh",

										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Facturas de Compra</p>
									<h3>{graphsData.expenseInvoiceCount[0].invoice_count}</h3>
								</Col>

								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										marginBottom: "2.5vh",
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Facturas de Compra Anuladas</p>
									<h3>{graphsData.expenseCanceledInvoiceCount[0].invoice_count}</h3>
								</Col>

								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Facturas de Compra Excluídas (Contabilidad)</p>
									<h3>{graphsData.expenseExcludedInvoiceCount[0].invoice_count}</h3>
								</Col>
							</Row>
						</Col>
						<Col span={9}>
							<Divider style={{ margin: 0 }}>Top 10 Clientes</Divider>
						</Col>
						<Col span={6} />
						<Col span={9}>
							<Divider style={{ margin: 0 }}>Top 10 Proveedores</Divider>
						</Col>
						<Col
							span={9}
							style={{
								height: "35vh",
								background: antToken.colorBgLayout,
								borderRadius: 10
							}}
						>
							<ResponsivePie
								data={structureTop2(graphsData.topClients || [], "out")}
								margin={{ top: 40, right: 10, bottom: 30, left: 10 }}
								innerRadius={0.5}
								padAngle={0.7}
								cornerRadius={3}
								activeOuterRadiusOffset={8}
								enableArcLabels={false}
								colors={reversedGreens}
								arcLinkLabelsSkipAngle={10}
								arcLinkLabelsTextColor="#333333"
								arcLinkLabelsThickness={2}
								arcLinkLabelsColor={{ from: "color" }}
								arcLabelsSkipAngle={10}
								arcLabelsTextColor={{
									from: "color",
									modifiers: [
										[
											"darker",
											2
										]
									]
								}}
								legends={[]}
								tooltip={(datum) =>
									<div
										style={{
											padding: 12,
											color: "#000c17",
											background: "white"
										}}
									>{datum.datum.data.longLabel} - GTQ {formatNumber(datum.datum.data.value)}</div>
								}
							/>
						</Col>
						<Col span={1} />
						<Col span={4}>
							<Row>
								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										marginBottom: "2.5vh",
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Ventas</p>
									<h3>GTQ {formatNumber(parseFloat(sumarValoresTotales(graphsData.salesByMonth)).toFixed(2))}</h3>
								</Col>

								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										marginBottom: "2.5vh",
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Compras</p>
									<h3>GTQ {formatNumber(parseFloat(sumarValoresTotales(graphsData.expensesByMonth)).toFixed(2))}</h3>
								</Col>

								<Col
									span={24}
									style={{
										height: "10vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<p style={{ margin: 0, fontSize: "12px", color: "gray" }}>Diferencia</p>
									<h3>GTQ {formatNumber(parseFloat(sumarValoresTotales(graphsData.salesByMonth) - sumarValoresTotales(graphsData.expensesByMonth)).toFixed(2))}</h3>
								</Col>
							</Row>

						</Col>
						<Col span={1} />
						<Col
							span={9}
							style={{
								height: "35vh",
								background: antToken.colorBgLayout,
								borderRadius: 10
							}}
						>
							<ResponsivePie
								data={structureTop2(graphsData.topSuppliers || [], "in")}
								margin={{ top: 40, right: 10, bottom: 30, left: 10 }}
								innerRadius={0.5}
								padAngle={0.7}
								cornerRadius={3}
								activeOuterRadiusOffset={8}
								enableArcLabels={false}
								colors={reversedOranges}
								arcLinkLabelsSkipAngle={10}
								arcLinkLabelsTextColor="#333333"
								arcLinkLabelsThickness={2}
								arcLinkLabelsColor={{ from: "color" }}
								arcLabelsSkipAngle={10}
								arcLabelsTextColor={{
									from: "color",
									modifiers: [
										[
											"darker",
											2
										]
									]
								}}
								legends={[]}
								tooltip={(datum) =>
									<div
										style={{
											padding: 12,
											color: "#000c17",
											background: "white"
										}}
									>{datum.datum.data.longLabel} - GTQ {formatNumber(datum.datum.data.value)}</div>
								}
							/>
						</Col>
						<Col span={9}>
							<Divider style={{ margin: 0, marginRight: "1vw" }}>Top 5 Clientes por Mes</Divider>
						</Col>
						<Col span={6}>
							<Divider style={{ margin: 0, marginRight: "1vw" }}>Impuestos por Ventas</Divider>
						</Col>
						<Col span={9}>
							<Divider style={{ margin: 0, marginLeft: "1vw" }}>Top 5 Proveedores por Mes</Divider>
						</Col>
						<Col
							span={9}
							style={{
								height: "40vh",
								background: antToken.colorBgLayout,
								borderRadius: 10
							}}
						>
							<ResponsiveBar
								data={structureMonthlyClientRanking(graphsData.monthlyClientRanking || [], "in")}
								keys={Object.keys(structureMonthlyClientRanking(graphsData.monthlyClientRanking || [], "in")[0]).filter(key => key !== "month")}
								indexBy="month"
								margin={{ top: 20, right: 20, bottom: 80, left: 70 }}
								padding={0.3}
								valueScale={{ type: "linear" }}
								indexScale={{ type: "band", round: true }}
								colors={reversedGreens}
								borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
								axisTop={null}
								axisRight={null}
								axisBottom={{
									tickSize: 5,
									tickPadding: 5,
									tickRotation: 90,
									legend: "Mes",
									legendPosition: "middle",
									legendOffset: 60
								}}
								axisLeft={{
									tickSize: 5,
									tickPadding: 5,
									tickRotation: 0,
									legend: "Total",
									legendPosition: "middle",
									legendOffset: -50
								}}
								labelSkipWidth={12}
								labelSkipHeight={12}
								enableLabel={false}
								animate={false}
								tooltip={(point) =>
									<div
										style={{
											padding: 12,
											color: "#000c17",
											background: "white"
										}}
									>{point.id} - GTQ {formatNumber(point.value)}</div>
								}
							/>
							
								
							{/* <ResponsiveLine
								data={structureMonthlyClientRanking(graphsData.monthlyClientRanking || [], "in")}
								enableTouchCrosshair
								enableArea
								margin={{ top: 40, right: 40, bottom: 50, left: 60 }}
								yScale={{
									type: "linear",
									min: 0,
									max: "auto",
									stacked: true,
									reverse: false
								}}
								yFormat=" >-.2f"
								axisTop={null}
								axisRight={null}
								axisBottom={{
									tickSize: 5,
									tickPadding: 0,
									tickRotation: 0,
									legend: "Mes",
									legendOffset: 36,
									legendPosition: "middle",
									truncateTickAt: 0
								}}
								colors={reversedGreens}
								axisLeft={{
									tickSize: 5,
									tickPadding: 0,
									tickRotation: 0,
									legend: "Total",
									legendOffset: -40,
									legendPosition: "middle",
									truncateTickAt: 0
								}}
								enableGridX={false}
								enableGridY={true}
								lineWidth={0}
								pointSize={0}
								areaOpacity={1}
								pointColor={{ theme: "background" }}
								pointBorderWidth={2}
								pointBorderColor={{ from: "serieColor" }}
								pointLabel="data.yFormatted"
								pointLabelYOffset={-12}
								useMesh={true}
								legends={[]}
								enableSlices="x"
								sliceTooltip={(point) => {
									const validPoints = point.slice.points.filter(data => data.data.y !== 0);
									return (
										<div
											style={{
												padding: "12px",
												color: "#000c17",
												background: "white",
												borderRadius: "4px",
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-end", // Alinea los elementos (icono + texto) a la derecha
												pointerEvents: "none", // Asegura que no interfiera con otros elementos
												whiteSpace: "nowrap", // Evita que el texto se desborde
												overflow: "hidden",
												textOverflow: "ellipsis",
												textAlign: "right" // Alinea el texto dentro de cada div a la derecha
											}}
										>
											{validPoints.length === 0 ? (
												<div>No hay datos disponibles</div>
											) : (
												validPoints.map(data => (
													<div
														key={data.id}
														style={{
															display: "flex",
															alignItems: "center",
															marginBottom: "8px" // Espacio entre los elementos
														}}
													>
														<div>
															{data.data.xLabel} - {formatNumber(data.data.y)}
														</div>
														<div
															style={{
																width: "12px",
																height: "12px",
																backgroundColor: data.serieColor,
																marginLeft: "8px",
																borderRadius: "2px"
															}}
														></div>
													</div>
												))
											)}
										</div>

									);
								}}
							/> */}
						</Col>
						<Col span={1} />
						<Col span={4}>
							<Row>
								<Col
									span={24}
									style={{
										height: "15vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<ResponsiveCirclePacking
										data={structureTaxOverview(graphsData.totalSalePayedTaxes, "in")}
										margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
										id="name"
										value="loc"
										colorBy="id"
										colors={schemeGreens[9].slice(3)}
										childColor={{
											from: "color",
											modifiers: [
												[
													"brighter",
													0.4
												]
											]
										}}
										valueFormat={value => `${formatNumber(value)}`}
										padding={4}
										enableLabels={true}
										label="id"
										labelTextColor={{
											from: "color",
											modifiers: [
												[
													"darker",
													2
												]
											]
										}}
										leavesOnly={true}
										borderWidth={0}
										zoomedId={zoomedId}
										motionConfig="slow"
										onClick={node => {
											setZoomedId(zoomedId === node.id ? null : node.id);
										}}
									/>
								</Col>
								<Col span={24}>
									<Divider style={{ margin: 0, marginRight: "1vw" }}>Impuestos por Compras</Divider>
								</Col>
								<Col
									span={24}
									style={{
										height: "21vh",
										background: antToken.colorBgLayout,
										borderRadius: 10,
										display: "flex",
										flexDirection: "column", // Organizar los elementos en columna
										justifyContent: "center", // Centrar horizontalmente
										alignItems: "center" // Centrar verticalmente
									}}
								>
									<ResponsiveCirclePacking
										data={structureTaxOverview(graphsData.totalExpensePayedTaxes, "out")}
										margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
										id="name"
										value="loc"
										colors={schemeOranges[9].slice(3)}
										colorBy="id"
										valueFormat={value => `${formatNumber(value)}`}
										padding={4}
										enableLabels={true}
										label="id"
										labelTextColor={{
											from: "color",
											modifiers: [
												[
													"darker",
													2
												]
											]

										}}
										leavesOnly={true}
										borderWidth={0}
										zoomedId={zoomedId}
										motionConfig="slow"
										onClick={node => {
											setZoomedId(zoomedId === node.id ? null : node.id);
										}}
									/>
								</Col>
							</Row>
						</Col>
						<Col span={1} />
						<Col
							span={9}
							style={{
								height: "40vh",
								background: antToken.colorBgLayout,
								borderRadius: 10
							}}
						>
							<ResponsiveBar
								data={structureMonthlyClientRanking(graphsData.monthlySupplierRanking || [], "out")}
								keys={Object.keys(structureMonthlyClientRanking(graphsData.monthlySupplierRanking || [], "out")[0]).filter(key => key !== "month")}
								indexBy="month"
								margin={{ top: 20, right: 20, bottom: 80, left: 70 }}
								padding={0.3}
								valueScale={{ type: "linear" }}
								indexScale={{ type: "band", round: true }}
								colors={reversedOranges}
								borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
								axisTop={null}
								axisRight={null}
								axisBottom={{
									tickSize: 5,
									tickPadding: 5,
									tickRotation: 90,
									legend: "Mes",
									legendPosition: "middle",
									legendOffset: 60
								}}
								axisLeft={{
									tickSize: 5,
									tickPadding: 5,
									tickRotation: 0,
									legend: "Total",
									legendPosition: "middle",
									legendOffset: -50
								}}
								labelSkipWidth={12}
								labelSkipHeight={12}
								enableLabel={false}
								animate={false}
								tooltip={(point) =>
									<div
										style={{
											padding: 12,
											color: "#000c17",
											background: "white"
										}}
									>{point.id} - GTQ {formatNumber(point.value)}</div>
								}
							/>
							{/* <ResponsiveLine
								data={structureMonthlyClientRanking(graphsData.monthlySupplierRanking || [], "out")}
								enableTouchCrosshair
								enableArea
								margin={{ top: 40, right: 40, bottom: 50, left: 60 }}
								yScale={{
									type: "linear",
									min: 0,
									max: "auto",
									stacked: true,
									reverse: false
								}}
								yFormat=" >-.2f"
								axisTop={null}
								axisRight={null}
								axisBottom={{
									tickSize: 5,
									tickPadding: 0,
									tickRotation: 0,
									legend: "Mes",
									legendOffset: 36,
									legendPosition: "middle",
									truncateTickAt: 0
								}}
								colors={reversedOranges}
								axisLeft={{
									tickSize: 5,
									tickPadding: 0,
									tickRotation: 0,
									legend: "Total",
									legendOffset: -40,
									legendPosition: "middle",
									truncateTickAt: 0
								}}
								enableGridX={false}
								enableGridY={true}
								lineWidth={0}
								pointSize={0}
								areaOpacity={1}
								pointColor={{ theme: "background" }}
								pointBorderWidth={2}
								pointBorderColor={{ from: "serieColor" }}
								pointLabel="data.yFormatted"
								pointLabelYOffset={-12}
								useMesh={true}
								legends={[]}
								enableSlices="x"
								sliceTooltip={(point) => {
									const validPoints = point.slice.points.filter(data => data.data.y !== 0);
									return (
										<div
											style={{
												padding: "12px",
												color: "#000c17",
												background: "white",
												borderRadius: "4px",
												display: "flex",
												flexDirection: "column",
												pointerEvents: "none", // Asegura que no interfiera con otros elementos
												whiteSpace: "nowrap", // Evita que el texto se desborde
												overflow: "hidden",
												textOverflow: "ellipsis"
											}}
										>
											{validPoints.length === 0 ? (
												<div>No hay datos disponibles</div>
											) : (
												validPoints.map(data => (
													<div
														key={data.id}
														style={{
															display: "flex",
															alignItems: "center",
															marginBottom: "8px" // Espacio entre los elementos
														}}
													>
														<div
															style={{
																width: "12px",
																height: "12px",
																backgroundColor: data.serieColor,
																marginRight: "8px",
																borderRadius: "2px"
															}}
														></div>
														{formatNumber(data.data.y)} - {data.data.xLabel}
													</div>
												))
											)}
										</div>
									);
								}}
							/> */}
						</Col>
						<Col span={1} />
					</Row>
				</>
			}
		</>
	);
};

DashBoardStart.propTypes = {
	token: PropTypes.string.isRequired,
	company: PropTypes.shape({
		id: PropTypes.string,
		tid: PropTypes.string,
		looker_link: PropTypes.string
	}).isRequired
};
